// @ts-nocheck
/**
 * 錬金メインエンジン
 *
 * 責務:
 * - 分解・作成・強化の統合管理
 * - 素材インベントリ管理
 * - 市場価格連動
 * - 状態の永続化
 */

import Decomposer from './Decomposer';
import Crafter from './Crafter';
import Enhancer from './Enhancer';
import { getMaterialInfo } from './data/materialTypes';

/**
 * 錬金エンジンクラス
 */
class AlchemyEngine {
  constructor() {
    // サブシステム
    this.decomposer = new Decomposer(this);
    this.crafter = new Crafter(this);
    this.enhancer = new Enhancer(this);

    // 素材インベントリ
    this.materials = new Map();

    // 作成した装備
    this.equipment = [];

    // 各種履歴（サブシステムで管理するものもあるがここに集約）
    this.decompositionHistory = [];

    // 統計
    this.stats = {
      totalDecomposed: 0,
      totalCrafted: 0,
      totalEnhanced: 0,
    };
  }

  // ========================================
  // カード分解
  // ========================================

  /**
   * カードを分解
   *
   * @param {Object} card - 分解するカード
   * @param {string} rarity - カードのレアリティ
   * @param {Object} options - オプション
   * @returns {{ success: boolean, materials: Object[], message: string }}
   */
  decomposeCard(card, rarity, options = {}) {
    const result = this.decomposer.decompose(card, rarity, options);
    if (result.success) {
      this.stats.totalDecomposed++;
    }
    return result;
  }

  /**
   * 複数カードを一括分解
   *
   * @param {Array<{ card: Object, rarity: string }>} cards - 分解するカード配列
   * @param {Object} options - オプション
   * @returns {{ success: boolean, results: Object[], totalMaterials: Object, message: string }}
   */
  decomposeMultiple(cards, options = {}) {
    const result = this.decomposer.decomposeMultiple(cards, options);
    if (result.success) {
      this.stats.totalDecomposed += result.results.filter(r => r.success).length;
    }
    return result;
  }

  /**
   * 分解プレビュー
   *
   * @param {Object} card - プレビューするカード
   * @param {string} rarity - カードのレアリティ
   * @param {Object} options - オプション
   * @returns {{ materials: Object[], totalValue: number }}
   */
  previewDecomposition(card, rarity, options = {}) {
    return this.decomposer.previewDecomposition(card, rarity, options);
  }

  // ========================================
  // 装備作成
  // ========================================

  /**
   * 装備を作成
   *
   * @param {string} recipeId - レシピID
   * @returns {{ success: boolean, equipment: Object|null, message: string }}
   */
  craftEquipment(recipeId) {
    const result = this.crafter.craft(recipeId);
    if (result.success) {
      this.equipment.push(result.equipment);
      this.stats.totalCrafted++;
    }
    return result;
  }

  /**
   * 作成可能なレシピ一覧を取得
   *
   * @param {Object} options - フィルタオプション
   * @returns {Object[]} レシピ一覧
   */
  getAvailableRecipes(options = {}) {
    return this.crafter.getAvailableRecipes(options);
  }

  /**
   * 作成プレビュー
   *
   * @param {string} recipeId - レシピID
   * @returns {Object|null} プレビュー情報
   */
  previewCraft(recipeId) {
    return this.crafter.previewCraft(recipeId);
  }

  /**
   * セット効果を計算
   *
   * @param {Object[]} equipments - 装備リスト
   * @returns {Object[]} 発動中のセット効果
   */
  calculateSetBonuses(equipments) {
    return this.crafter.calculateSetBonuses(equipments);
  }

  // ========================================
  // 装備強化
  // ========================================

  /**
   * 装備を強化
   *
   * @param {Object} equipment - 強化する装備
   * @param {string} enhanceType - 強化タイプ ('level' | 'awakening')
   * @returns {{ success: boolean, equipment: Object|null, message: string }}
   */
  enhanceEquipment(equipment, enhanceType) {
    const result = this.enhancer.enhance(equipment, enhanceType);
    if (result.success) {
      // 装備リストを更新
      const index = this.equipment.findIndex(eq => eq.id === equipment.id);
      if (index !== -1) {
        this.equipment[index] = result.equipment;
      }
      this.stats.totalEnhanced++;
    }
    return result;
  }

  /**
   * 装備をレベルアップ
   *
   * @param {Object} equipment - 強化する装備
   * @returns {{ success: boolean, equipment: Object|null, message: string }}
   */
  levelUpEquipment(equipment) {
    return this.enhanceEquipment(equipment, 'level');
  }

  /**
   * 装備を覚醒
   *
   * @param {Object} equipment - 強化する装備
   * @returns {{ success: boolean, equipment: Object|null, message: string }}
   */
  awakenEquipment(equipment) {
    return this.enhanceEquipment(equipment, 'awakening');
  }

  /**
   * 強化プレビュー
   *
   * @param {Object} equipment - 装備
   * @param {string} enhanceType - 'level' | 'awakening'
   * @returns {Object|null} プレビュー情報
   */
  previewEnhancement(equipment, enhanceType) {
    return this.enhancer.previewEnhance(equipment, enhanceType);
  }

  /**
   * 装備の強化可能状態を取得
   *
   * @param {Object} equipment - 装備
   * @returns {Object} 各強化タイプの可否
   */
  getEnhanceAvailability(equipment) {
    return this.enhancer.getEnhanceAvailability(equipment);
  }

  // ========================================
  // 素材管理
  // ========================================

  /**
   * 素材を追加
   *
   * @param {string} materialType - 素材タイプ
   * @param {number} count - 追加数
   */
  addMaterial(materialType, count) {
    if (count <= 0) return;
    const current = this.materials.get(materialType) || 0;
    this.materials.set(materialType, current + count);
  }

  /**
   * 素材を消費
   *
   * @param {string} materialType - 素材タイプ
   * @param {number} count - 消費数
   * @returns {boolean} 成功したかどうか
   */
  consumeMaterial(materialType, count) {
    if (count <= 0) return true;
    const current = this.materials.get(materialType) || 0;
    if (current < count) return false;
    this.materials.set(materialType, current - count);
    return true;
  }

  /**
   * 素材数を取得
   *
   * @param {string} materialType - 素材タイプ
   * @returns {number} 所持数
   */
  getMaterialCount(materialType) {
    return this.materials.get(materialType) || 0;
  }

  /**
   * 全素材を取得
   *
   * @returns {Object} 素材タイプをキーとした所持数
   */
  getAllMaterials() {
    return Object.fromEntries(this.materials);
  }

  /**
   * 素材情報付きで全素材を取得
   *
   * @returns {Object[]} 素材情報配列
   */
  getAllMaterialsWithInfo() {
    const materials = [];
    for (const [type, count] of this.materials) {
      const info = getMaterialInfo(type);
      materials.push({
        type,
        count,
        info: info || { name: type, tier: 'common', icon: '📦' },
      });
    }

    // ティア順でソート
    const tierOrder = { legendary: 0, epic: 1, rare: 2, uncommon: 3, common: 4 };
    materials.sort((a, b) => {
      const tierDiff = (tierOrder[a.info.tier] || 5) - (tierOrder[b.info.tier] || 5);
      if (tierDiff !== 0) return tierDiff;
      return b.count - a.count;
    });

    return materials;
  }

  /**
   * 特定ティアの素材を取得
   *
   * @param {string} tier - ティア
   * @returns {Object[]} 素材情報配列
   */
  getMaterialsByTier(tier) {
    return this.getAllMaterialsWithInfo().filter(m => m.info.tier === tier);
  }

  /**
   * 素材を使用するレシピを取得
   *
   * @param {string} materialType - 素材タイプ
   * @returns {Object[]} レシピ配列
   */
  getRecipesForMaterial(materialType) {
    return this.crafter.getRecipesForMaterial(materialType);
  }

  // ========================================
  // 装備管理
  // ========================================

  /**
   * 全装備を取得
   *
   * @returns {Object[]} 装備リスト
   */
  getAllEquipment() {
    return [...this.equipment];
  }

  /**
   * IDで装備を取得
   *
   * @param {string} equipmentId - 装備ID
   * @returns {Object|null} 装備
   */
  getEquipmentById(equipmentId) {
    return this.equipment.find(eq => eq.id === equipmentId) || null;
  }

  /**
   * タイプ別装備を取得
   *
   * @param {string} type - 装備タイプ
   * @returns {Object[]} 装備リスト
   */
  getEquipmentByType(type) {
    return this.equipment.filter(eq => eq.type === type);
  }

  /**
   * 装備を削除
   *
   * @param {string} equipmentId - 装備ID
   * @returns {boolean} 成功したかどうか
   */
  removeEquipment(equipmentId) {
    const index = this.equipment.findIndex(eq => eq.id === equipmentId);
    if (index !== -1) {
      this.equipment.splice(index, 1);
      return true;
    }
    return false;
  }

  /**
   * 装備をロック/アンロック
   *
   * @param {string} equipmentId - 装備ID
   * @param {boolean} locked - ロック状態
   */
  setEquipmentLock(equipmentId, locked) {
    const equipment = this.getEquipmentById(equipmentId);
    if (equipment) {
      equipment.locked = locked;
    }
  }

  // ========================================
  // 統計・履歴
  // ========================================

  /**
   * 統計を取得
   *
   * @returns {Object} 統計情報
   */
  getStats() {
    return {
      ...this.stats,
      totalMaterialTypes: this.materials.size,
      totalMaterialCount: Array.from(this.materials.values()).reduce((a, b) => a + b, 0),
      totalEquipment: this.equipment.length,
    };
  }

  /**
   * 分解履歴を取得
   *
   * @returns {Object[]} 分解履歴
   */
  getDecompositionHistory() {
    return [...this.decompositionHistory];
  }

  /**
   * 作成履歴を取得
   *
   * @returns {Object[]} 作成履歴
   */
  getCraftHistory() {
    return this.crafter.getCraftHistory();
  }

  /**
   * 強化履歴を取得
   *
   * @returns {Object[]} 強化履歴
   */
  getEnhanceHistory() {
    return this.enhancer.getEnhanceHistory();
  }

  // ========================================
  // シリアライズ
  // ========================================

  /**
   * 状態の保存
   *
   * @returns {Object} シリアライズされた状態
   */
  serialize() {
    return {
      materials: Object.fromEntries(this.materials),
      equipment: this.equipment,
      stats: this.stats,
      decompositionHistory: this.decompositionHistory,
      crafter: this.crafter.serialize(),
      enhancer: this.enhancer.serialize(),
    };
  }

  /**
   * 状態の復元
   *
   * @param {Object} data - シリアライズされた状態
   */
  deserialize(data) {
    if (data.materials) {
      this.materials = new Map(Object.entries(data.materials));
    }
    if (data.equipment) {
      this.equipment = data.equipment;
    }
    if (data.stats) {
      this.stats = { ...this.stats, ...data.stats };
    }
    if (data.decompositionHistory) {
      this.decompositionHistory = data.decompositionHistory;
    }
    if (data.crafter) {
      this.crafter.deserialize(data.crafter);
    }
    if (data.enhancer) {
      this.enhancer.deserialize(data.enhancer);
    }
  }

  /**
   * 状態をリセット
   */
  reset() {
    this.materials.clear();
    this.equipment = [];
    this.decompositionHistory = [];
    this.stats = {
      totalDecomposed: 0,
      totalCrafted: 0,
      totalEnhanced: 0,
    };
    this.crafter = new Crafter(this);
    this.enhancer = new Enhancer(this);
    this.decomposer = new Decomposer(this);
  }

  // ========================================
  // playerData連携
  // ========================================

  /**
   * playerDataから素材をインポート
   *
   * @param {Object} twinKeyData - playerData.twinKeyData
   */
  importFromPlayerData(twinKeyData) {
    // 素材をインポート
    if (twinKeyData.materials) {
      for (const [type, count] of Object.entries(twinKeyData.materials)) {
        this.materials.set(type, count);
      }
    }

    // 装備をインポート
    if (twinKeyData.equipment) {
      this.equipment = [...twinKeyData.equipment];
    }
  }

  /**
   * playerDataに素材をエクスポート
   *
   * @returns {Object} エクスポート用データ
   */
  exportToPlayerData() {
    return {
      materials: Object.fromEntries(this.materials),
      equipment: this.equipment,
    };
  }
}

// シングルトンインスタンス
export const alchemyEngine = new AlchemyEngine();
export default AlchemyEngine;
